import React, { useCallback, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, useWindowDimensions } from 'react-native';
import { Image, type ImageLoadEventData } from 'expo-image';
import { Heart, MessageCircle, Share2 } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, ON_COLOR, TYPOGRAPHY, withWeight } from '../../theme';
import { Avatar, ContentActionMenu, DotLabel } from '../common';
import { LinkifiedText } from './LinkifiedText';
import { resolveAfterImage, resolveBeforeImage, isPairPost } from '../../utils/beforeAfter';
import { formatRelativeTime, formatShortDate } from '../../utils/time';
import { normalizePostCategory } from '../../types';
import { sharePost } from '../../utils/share';
import { useAuthStore } from '../../store/authStore';
import { useUIStore } from '../../store/uiStore';
import { usePostsStore } from '../../store/postsStore';
import { useDesktopShell } from '../../hooks/useDesktopShell';

interface PostCardProps {
  post: import('../../types').Post;
  isLiked?: boolean;
  variant?: 'feed' | 'card';
  /** Render the full-resolution original (detail view) instead of the
      400x400 thumbnail, uncropped inside a fixed-height stage. */
  fullImage?: boolean;
  currentUserId?: string;
  onPress?: () => void;
  onLike?: () => void;
  onComment?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

// Plaud grammar: posts are card-less entries. The feed variant is a
// hairline-divided row; the card variant (desktop grid) is a hairline-
// bordered box. Category renders as a dot-label in the header, media sits
// in a 12px-radius frame, and address + checklist collapse into one gray
// metadata line. No category tint anywhere.
/** A paragraph that's wholly one quotation ("…" or “…”), as plan-update
 *  News posts end with the party's own words. */
function isQuote(paragraph: string): boolean {
  const t = paragraph.trim();
  return t.length > 2 && /^["“]/.test(t) && /["”]$/.test(t);
}

/** A News post's body on its own page: paragraphs at the reading size, and a
 *  quoted paragraph set off as a pull quote with a left rule (Sept 28). */
function NewsBody({ text }: { text: string }) {
  const { colors } = useTheme();
  const paragraphs = text.split(/\n{2,}/).filter((p) => p.trim().length > 0);
  return (
    <View style={styles.newsBody}>
      {paragraphs.map((paragraph, i) =>
        isQuote(paragraph) ? (
          <View key={i} style={[styles.newsQuote, { borderLeftColor: colors.borderStrong }]}>
            <LinkifiedText style={[styles.newsParagraph, { color: colors.textSecondary }]}>
              {paragraph.trim()}
            </LinkifiedText>
          </View>
        ) : (
          <LinkifiedText key={i} style={[styles.newsParagraph, { color: colors.text }]}>
            {paragraph.trim()}
          </LinkifiedText>
        )
      )}
    </View>
  );
}

function PostCardImpl({
  post,
  isLiked = false,
  variant = 'feed',
  fullImage = false,
  currentUserId,
  onPress,
  onLike,
  onComment,
  onEdit,
  onDelete,
}: PostCardProps) {
  const { colors } = useTheme();
  const { height: windowHeight } = useWindowDimensions();
  const isDesktopShell = useDesktopShell();
  const isCard = variant === 'card';
  // While a delete's undo toast is up (useCommentThread's handleDelete,
  // deferredCommit) the real decrement hasn't landed yet, so this icon
  // would otherwise keep counting a comment the panel below no longer
  // shows — see InlineComments' matching subtraction.
  const pendingHiddenCommentCount = usePostsStore(
    (s) => Object.keys(s.pendingHiddenCommentIds[post.id] ?? {}).length
  );
  const displayCommentsCount = Math.max(0, post.commentsCount - pendingHiddenCommentCount);
  // Detail view: the stage's initial height (before the photo's own ratio
  // is known) and the cap once it is — a portrait photo can never grow
  // taller than ~60% of the screen either way.
  const detailImageHeight = Math.min(Math.max(windowHeight * 0.6, 280), 620);

  const displayName = post.isAnonymous ? 'Fire Scout' : post.authorName;
  const displayType = normalizePostCategory(post.category);
  // Feed/card variants render small — prefer the 400x400 WebP thumbnail
  // generated by the Resize Images extension when available, and fall back
  // to the original while the async resize is still in flight. The detail
  // view (fullImage) flips the priority: thumbnails upscaled to content
  // width read as blurry.
  // Both fallback chains live in src/utils/beforeAfter.ts, shared with the
  // printed readiness record so there is one place that knows how the two
  // pairing mechanisms resolve.
  const displayImage = resolveAfterImage(post, fullImage);
  const beforeImage = resolveBeforeImage(post, fullImage);
  // While the heavier original streams in, show the (usually cached)
  // thumbnail underneath instead of a blank surface.
  const displayPlaceholder = fullImage ? post.thumbnailUrl : undefined;
  const beforePlaceholder = fullImage
    ? post.beforePostThumbnailUrl || post.beforeThumbnailUrl
    : undefined;
  const isPair = Boolean(isPairPost(post) && beforeImage && displayImage);
  // News posts are text-first and may carry no photo at all (imageUrl is
  // optional). No image block, no blur-fill stage — the caption alone
  // becomes the card's main content, set larger for readability.
  const hasImage = Boolean(displayImage);
  // Grey bands (Sept 29 2026, M4): the post schema doesn't store a photo's
  // width/height, so its ratio is unknown until the image itself reports
  // one. Until then (and until the frame's own width is measured) the
  // stage keeps its old fixed-height, contain-inside-a-blurred-backdrop
  // look — a brief layout jump when the real size lands beats guessing
  // wrong and cropping someone's photo.
  const [stageWidth, setStageWidth] = useState(0);
  const [photoRatio, setPhotoRatio] = useState<{ url: string; ratio: number } | null>(null);
  const resolvedPhotoRatio =
    photoRatio && photoRatio.url === displayImage ? photoRatio.ratio : null;
  const handlePhotoLoad = useCallback(
    (e: ImageLoadEventData) => {
      if (e.source.width > 0 && e.source.height > 0) {
        setPhotoRatio({ url: displayImage ?? '', ratio: e.source.width / e.source.height });
      }
    },
    [displayImage]
  );
  const stageHeight =
    resolvedPhotoRatio && stageWidth > 0
      ? Math.min(stageWidth / resolvedPhotoRatio, detailImageHeight)
      : detailImageHeight;
  const isAuthor = currentUserId != null && post.authorId === currentUserId;
  // Moderators get a delete-only menu on anyone's post (curation, Aug 2026);
  // editing stays author-only, mirroring firestore.rules.
  const isModerator = useAuthStore((s) => s.isModerator);
  const showMenu = onDelete != null && (isAuthor ? onEdit != null : isModerator);
  const isEdited =
    post.updatedAt != null && post.createdAt.toMillis() !== post.updatedAt.toMillis();
  // News is dated content: absolute date, matching the News tab's kicker
  // (NewsItem). Observations keep the relative form.
  const displayTimestamp =
    (displayType === 'news'
      ? formatShortDate(post.createdAt)
      : formatRelativeTime(post.createdAt, { suffix: 'ago' })) + (isEdited ? ' (edited)' : '');
  const displayLocation = post.location?.address;
  // Address and checklist collapse into one gray metadata line
  const metaLine = [
    displayLocation,
    post.checklistItemLabel ? `Checklist: ${post.checklistItemLabel}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <View
      style={[
        styles.container,
        isCard
          ? [styles.cardContainer, styles.fill, { borderColor: colors.border }]
          : // The detail view (fullImage) draws no rule of its own: the
            // first thing under it (a row link, or the comments) opens with
            // one. Both drawing one stacked two lines on a News post, which
            // has no row links (Sept 28).
            fullImage
            ? null
            : { borderBottomWidth: 1, borderBottomColor: colors.border },
      ]}>
      {/* Detail view: the mockup's labeled metadata block (Author / Category)
          under the screen's big light title, replacing the avatar chip and the
          right-aligned category dot. Feed and card variants keep the chip
          header — a list needs the face and the timestamp at a glance. */}
      {fullImage && displayType === 'news' ? (
        // A News post's page (Sept 28): a plain byline, avatar, name and
        // date, instead of the observation's labeled Author / Category rows.
        // The page puts "• News" above the title, so no category here.
        <View style={styles.detailHead}>
          <View style={styles.byline}>
            <Avatar size={28} name={displayName} imageUrl={post.authorAvatarUrl ?? undefined} />
            <Text style={styles.who} numberOfLines={1}>
              <Text style={[styles.bylineName, { color: colors.text }]}>{displayName}</Text>
              <Text
                style={[styles.bylineDate, { color: colors.textSecondary }]}
                // @ts-expect-error dataSet is a react-native-web prop (visual-audit CSS masking hook); not in RN's type defs
                dataSet={{ visualmask: 'true' }}>
                {'  ·  '}
                {displayTimestamp}
              </Text>
            </Text>
          </View>
          {showMenu && onDelete && (
            <ContentActionMenu onEdit={isAuthor ? onEdit : undefined} onDelete={onDelete} />
          )}
        </View>
      ) : fullImage ? (
        <View style={styles.detailHead}>
          <View style={styles.metaBlock}>
            <Text style={[styles.metaLabelLine, { color: colors.textSecondary }]}>
              <Text style={styles.metaKey}>Author: </Text>
              {displayName}
              <Text
                // @ts-expect-error dataSet is a react-native-web prop (visual-audit CSS masking hook); not in RN's type defs
                dataSet={{ visualmask: 'true' }}>
                {' · '}
                {displayTimestamp}
              </Text>
            </Text>
            <View style={styles.metaRow}>
              <Text style={[styles.metaLabelLine, styles.metaKey, { color: colors.textSecondary }]}>
                Category:
              </Text>
              <DotLabel
                label={
                  displayType === 'progress'
                    ? 'Progress'
                    : displayType === 'news'
                      ? 'News'
                      : 'Opportunity'
                }
                variant={displayType}
              />
              {post.checklistItemLabel ? (
                <Text
                  style={[styles.metaLabelLine, styles.metaFlex, { color: colors.textSecondary }]}
                  numberOfLines={1}>
                  <Text style={styles.metaKey}>· Checklist: </Text>
                  {post.checklistItemLabel}
                </Text>
              ) : null}
            </View>
            {/* The address is the screen title, so it only earns a line here
                when a location edit has moved it away from that title. */}
            {displayLocation && displayLocation !== post.address ? (
              <Text
                style={[styles.metaLabelLine, { color: colors.textSecondary }]}
                numberOfLines={1}>
                <Text style={styles.metaKey}>Location: </Text>
                {displayLocation}
              </Text>
            ) : null}
          </View>
          {showMenu && onDelete && (
            <ContentActionMenu onEdit={isAuthor ? onEdit : undefined} onDelete={onDelete} />
          )}
        </View>
      ) : (
        /* Header — 24px avatar, name · time, category dot-label */
        <View style={styles.header}>
          <Avatar size={24} name={displayName} imageUrl={post.authorAvatarUrl ?? undefined} />
          <Text style={styles.who} numberOfLines={1}>
            <Text style={[styles.authorName, { color: colors.text }]}>{displayName}</Text>
            <Text
              style={[styles.meta, { color: colors.textTertiary }]}
              // @ts-expect-error dataSet is a react-native-web prop (visual-audit CSS masking hook); not in RN's type defs
              dataSet={{ visualmask: 'true' }}>
              {'  ·  '}
              {displayTimestamp}
            </Text>
          </Text>
          <DotLabel
            label={
              displayType === 'progress'
                ? 'Progress'
                : displayType === 'news'
                  ? 'News'
                  : 'Opportunity'
            }
            variant={displayType}
          />
          {showMenu && onDelete && (
            <ContentActionMenu onEdit={isAuthor ? onEdit : undefined} onDelete={onDelete} />
          )}
        </View>
      )}

      {/* Media + caption + meta. Text-only cards grow the pressable to fill
          the card: in the desktop grid, rows stretch to the tallest sibling,
          and without this the space under a short caption is dead — a tap
          there should open the post like anywhere else on the card. */}
      <TouchableOpacity
        onPress={onPress}
        activeOpacity={0.95}
        style={!hasImage && styles.pressGrow}
        accessibilityRole="button"
        accessibilityLabel="View post">
        {isPair ? (
          // @ts-expect-error dataSet is a react-native-web prop (visual-audit CSS masking hook); not in RN's type defs
          <View style={[styles.media, styles.pairRow]} dataSet={{ visualmask: 'true' }}>
            <View style={styles.pairHalf}>
              <Image
                source={beforeImage}
                placeholder={beforePlaceholder}
                style={[styles.pairImage, { backgroundColor: colors.surface }]}
                contentFit="cover"
                cachePolicy="memory-disk"
                transition={150}
              />
              <View style={[styles.imageTag, { backgroundColor: colors.overlay }]}>
                <Text style={styles.imageTagText}>
                  {post.beforePostCreatedAt
                    ? `Before · ${formatRelativeTime(post.beforePostCreatedAt, { suffix: 'ago' })}`
                    : 'Before'}
                </Text>
              </View>
            </View>
            <View style={styles.pairHalf}>
              <Image
                source={displayImage}
                placeholder={displayPlaceholder}
                style={[styles.pairImage, { backgroundColor: colors.surface }]}
                contentFit="cover"
                cachePolicy="memory-disk"
                transition={150}
              />
              <View style={[styles.imageTag, { backgroundColor: colors.overlay }]}>
                <Text style={styles.imageTagText}>After</Text>
              </View>
            </View>
          </View>
        ) : fullImage && displayImage ? (
          <View
            onLayout={(e) => setStageWidth(e.nativeEvent.layout.width)}
            style={[
              styles.media,
              styles.imageStage,
              { height: stageHeight, backgroundColor: colors.surface },
            ]}
            // @ts-expect-error dataSet is a react-native-web prop (visual-audit CSS masking hook); not in RN's type defs
            dataSet={{ visualmask: 'true' }}>
            {/* Ambient backdrop: the photo's own colors, blurred, fill the
                stage edge-to-edge so the uncropped frame never sits against
                empty bars. Sourced from the thumbnail — it's blurred anyway
                and usually already cached from the feed. */}
            <Image
              source={displayPlaceholder || displayImage}
              style={styles.stageBackdrop}
              contentFit="cover"
              blurRadius={50}
              cachePolicy="memory-disk"
            />
            <Image
              source={displayImage}
              placeholder={displayPlaceholder}
              style={styles.stageImage}
              contentFit="contain"
              placeholderContentFit="contain"
              cachePolicy="memory-disk"
              transition={200}
              onLoad={handlePhotoLoad}
            />
          </View>
        ) : (
          displayImage && (
            <View style={styles.media}>
              <Image
                source={displayImage}
                style={[styles.image, { backgroundColor: colors.surface }]}
                contentFit="cover"
                cachePolicy="memory-disk"
                transition={150}
                // @ts-expect-error dataSet is a react-native-web prop (visual-audit CSS masking hook); not in RN's type defs
                dataSet={{ visualmask: 'true' }}
              />
            </View>
          )
        )}
        {post.caption ? (
          // News captions are announcements with links (tappable via
          // LinkifiedText); observation captions stay plain text.
          displayType === 'news' ? (
            fullImage && !hasImage ? (
              <NewsBody text={post.caption} />
            ) : (
              <LinkifiedText
                style={[
                  styles.caption,
                  !hasImage && styles.captionNoImage,
                  { color: colors.text },
                ]}>
                {post.caption}
              </LinkifiedText>
            )
          ) : (
            <Text
              style={[styles.caption, !hasImage && styles.captionNoImage, { color: colors.text }]}>
              {post.caption}
            </Text>
          )
        ) : null}
        {/* Detail view carries address + checklist in the metadata block
            above, so the trailing gray line is feed/card only. */}
        {!fullImage && metaLine ? (
          // textSecondary keeps the gray register AA-legible (audit:a11y);
          // textTertiary at 11px fails contrast.
          <Text numberOfLines={1} style={[styles.metaLine, { color: colors.textSecondary }]}>
            {metaLine}
          </Text>
        ) : null}
      </TouchableOpacity>

      {/* Actions */}
      <View style={[styles.actions, fullImage && displayType === 'news' && styles.actionsNews]}>
        <TouchableOpacity
          onPress={onLike}
          style={[
            styles.actionButton,
            isDesktopShell ? styles.actionButtonHitboxDesktop : styles.actionButtonHitboxPhone,
            !isDesktopShell && styles.actionButtonHitboxPhoneFirst,
          ]}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel={isLiked ? 'Unlike post' : 'Like post'}
          accessibilityState={{ selected: isLiked }}>
          <Heart
            size={16}
            color={isLiked ? colors.text : colors.textSecondary}
            fill={isLiked ? colors.text : 'none'}
            strokeWidth={1.5}
          />
          {post.likesCount > 0 && (
            <Text
              style={[styles.actionCount, { color: isLiked ? colors.text : colors.textSecondary }]}>
              {post.likesCount}
            </Text>
          )}
        </TouchableOpacity>
        <TouchableOpacity
          onPress={onComment}
          style={[
            styles.actionButton,
            isDesktopShell ? styles.actionButtonHitboxDesktop : styles.actionButtonHitboxPhone,
          ]}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="View comments"
          accessibilityHint={
            displayCommentsCount > 0 ? `${displayCommentsCount} comments` : undefined
          }>
          <MessageCircle size={16} color={colors.textSecondary} strokeWidth={1.5} />
          {displayCommentsCount > 0 && (
            <Text style={[styles.actionCount, { color: colors.textSecondary }]}>
              {displayCommentsCount}
            </Text>
          )}
        </TouchableOpacity>
        {/* Detail view docks share in the header (mockup dethead), so the
            row would otherwise offer it twice on the same screen. */}
        {!fullImage && (
          <TouchableOpacity
            onPress={() => {
              void sharePost({ postId: post.id, caption: post.caption }).then((result) => {
                if (result === 'copied')
                  useUIStore.getState().showToast({ message: 'Link copied' });
                if (result === 'failed')
                  useUIStore.getState().showToast({ message: "Couldn't copy the link" });
              });
            }}
            style={[
              styles.actionButton,
              isDesktopShell ? styles.actionButtonHitboxDesktop : styles.actionButtonHitboxPhone,
            ]}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel="Share post">
            <Share2 size={16} color={colors.textSecondary} strokeWidth={1.5} />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.smd + 2,
    paddingBottom: SPACING.smd,
  },
  cardContainer: {
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.md,
  },
  fill: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  who: {
    flex: 1,
  },
  detailHead: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: SPACING.sm,
  },
  metaBlock: {
    flex: 1,
    gap: 2,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs + 1,
  },
  metaFlex: {
    flexShrink: 1,
  },
  metaLabelLine: {
    ...TYPOGRAPHY.metadata,
  },
  // Label stands out against its value (Author:/Category:/Location:) —
  // withWeight instead of a hand-rolled fontFamily override, same faces.
  metaKey: {
    ...withWeight(TYPOGRAPHY.metadata, 'medium'),
  },
  authorName: {
    ...TYPOGRAPHY.bodySmall,
    fontFamily: TYPOGRAPHY.h4.fontFamily, // semibold name
  },
  meta: {
    ...TYPOGRAPHY.bodySmall,
  },
  media: {
    marginTop: SPACING.sm + 2,
    borderRadius: BORDER_RADIUS.md,
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    aspectRatio: 4 / 3,
  },
  // Detail view. Height comes from detailImageHeight at render time
  // (viewport-derived).
  imageStage: {
    width: '100%',
  },
  // Scaled past the edges so the blur doesn't feather to transparency at
  // the stage border; dimmed toward the surface color underneath so the
  // sharp foreground stays dominant.
  stageBackdrop: {
    ...StyleSheet.absoluteFillObject,
    opacity: 0.8,
    transform: [{ scale: 1.2 }],
  },
  stageImage: {
    flex: 1,
  },
  pairRow: {
    flexDirection: 'row',
    gap: 2,
  },
  pairHalf: {
    flex: 1,
  },
  // Each half is half the card width; 2:3 keeps the pair row the same
  // height as the single-image 4:3 layout so feed cards stay uniform.
  pairImage: {
    width: '100%',
    aspectRatio: 2 / 3,
  },
  imageTag: {
    position: 'absolute',
    top: SPACING.sm,
    left: SPACING.sm,
    borderRadius: BORDER_RADIUS.full,
    paddingVertical: 3,
    paddingHorizontal: SPACING.sm + 1,
  },
  imageTagText: {
    ...TYPOGRAPHY.caption,
    color: ON_COLOR.text,
  },
  caption: {
    ...TYPOGRAPHY.body,
    marginTop: SPACING.sm + 1,
  },
  // Text-first cards (no image): the caption is the whole card, so it reads
  // one size up from the normal in-card caption line.
  captionNoImage: {
    ...TYPOGRAPHY.h4,
  },
  // A News post's own page reads as body text, not the card's heading-weight
  // caption (all bold read as shouting): the reading size, regular weight,
  // room between lines (Sept 28).
  newsBody: {
    marginTop: SPACING.md,
    gap: SPACING.md,
  },
  newsParagraph: {
    ...TYPOGRAPHY.reading,
  },
  newsQuote: {
    borderLeftWidth: 2,
    paddingLeft: SPACING.md,
  },
  byline: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  bylineName: {
    ...TYPOGRAPHY.label,
  },
  bylineDate: {
    ...TYPOGRAPHY.caption,
  },
  // Text-only cards: the pressable absorbs the stretch a grid row adds, so
  // the whole card face opens the post (see the JSX comment).
  pressGrow: {
    flexGrow: 1,
  },
  metaLine: {
    ...TYPOGRAPHY.metadata,
    marginTop: SPACING.sm,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: SPACING.sm + 2,
    gap: SPACING.md + 2,
  },
  actionsNews: {
    marginTop: SPACING.lg,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.xs + 1,
  },
  // Hit area, RN-web ignores hitSlop (Sept 29 2026, F2): padding grows the
  // touchable to 32px square (16px icon + 8px each side); an equal negative
  // margin cancels it back out of the layout so the row's spacing and the
  // icon's own position don't move. The three icons' 33px spacing (gap +
  // icon width) exactly fits a 32px box without overlapping its neighbor.
  actionButtonHitboxDesktop: {
    padding: SPACING.sm,
    margin: -SPACING.sm,
  },
  // 44px square for a touch target. Only the vertical padding is cancelled
  // with a negative margin (so the row's height doesn't grow) — the
  // horizontal padding is left real, so consecutive buttons push apart
  // instead of overlapping (a 44px box doesn't fit in the old 33px
  // spacing). "It's fine that they spread out" — the row just gets wider.
  actionButtonHitboxPhone: {
    padding: SPACING.smd + 2,
    marginVertical: -(SPACING.smd + 2),
  },
  // Only the first (leftmost) button also cancels its left padding, so the
  // row's own left edge stays where the like icon visually sat before.
  actionButtonHitboxPhoneFirst: {
    marginLeft: -(SPACING.smd + 2),
  },
  actionCount: {
    ...TYPOGRAPHY.bodySmall,
  },
});

export const PostCard = React.memo(PostCardImpl);
